import { useRef, useState } from 'react'
import { ScanLine, Upload, Loader2, AlertCircle, Check, Paperclip, X } from 'lucide-react'
import { uploadExpenseFile, periodFolder } from '../../lib/expenseUpload.js'

/**
 * Attach-first intake, shown at the top of a NEW expense.
 *
 * Attach the document, then press อ่านข้อมูลจากเอกสาร — reading is never
 * automatic. A 25-second read that starts on its own and throws a dialog over
 * the form is an interruption; pressing a button makes it something the user
 * asked for and can wait on.
 *
 * Files land in the same `files` state the ไฟล์แนบ section at the bottom uses,
 * so nothing is uploaded twice and the record has one list of attachments.
 */
export default function ScanIntake({ form, files, setFiles, onScan, ocrBusy, ocrDone, ocrError, onDismiss }) {
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)
  const inputRef = useRef(null)

  const scannable = (f) => /pdf|image/i.test(f?.type || '') || /\.(pdf|png|jpe?g|webp|gif)$/i.test(f?.name || '')
  const candidates = files.filter((f) => f.location && scannable(f))
  const anyRead = candidates.some((f) => ocrDone?.has(f.location))

  async function onFiles(list) {
    const arr = Array.from(list || [])
    if (!arr.length) return
    setUploading(true); setErr(null)
    const ym = periodFolder(form.date_buy)
    try {
      for (const f of arr) {
        const entry = await uploadExpenseFile(f, ym)
        setFiles((cur) => [...cur, entry])
      }
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <section className="rounded-2xl border border-brand-200 bg-brand-50/40 p-5">
      {/* One input for the whole panel. Nesting a second one inside the
          "แนบเอกสารเพิ่ม" button would be invalid markup and would re-fire the
          click through the button it sits in. */}
      <input
        ref={inputRef} type="file" multiple accept="image/*,application/pdf,.pdf"
        className="hidden" onChange={(e) => onFiles(e.target.files)}
      />
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-white ring-1 ring-brand-100 text-brand-600 flex items-center justify-center flex-none">
          <ScanLine size={18} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-semibold text-slate-900">เริ่มจากเอกสาร</div>
          <div className="text-[12.5px] text-slate-500 mt-0.5">
            แนบใบเสร็จหรือใบกำกับภาษี แล้วให้ระบบอ่านและกรอกข้อมูลให้ — ตรวจสอบก่อนบันทึกได้เสมอ
          </div>
        </div>
        {onDismiss && (
          <button
            type="button" onClick={onDismiss} title="ซ่อน — กรอกเอง"
            className="p-1.5 -m-1 text-slate-400 hover:text-slate-600 rounded-lg flex-none"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {!candidates.length ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files) }}
          onClick={() => inputRef.current?.click()}
          className="mt-4 rounded-xl border border-dashed border-brand-300 bg-white/70 hover:bg-white transition cursor-pointer py-8 text-center"
        >
          {uploading
            ? <div className="text-[13px] text-slate-500"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังอัปโหลด…</div>
            : <div className="text-slate-500">
                <Upload size={22} className="mx-auto mb-2 text-brand-400" />
                <div className="text-[13.5px] font-medium text-slate-700">คลิกหรือลากเอกสารมาวางที่นี่</div>
                <div className="text-[12px] text-slate-400 mt-0.5">รองรับรูปภาพและ PDF</div>
              </div>}
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {candidates.map((f) => {
            const busy = ocrBusy === f.location
            const done = ocrDone?.has(f.location)
            return (
              <div key={f.uuid || f.location} className="flex items-center gap-3 rounded-xl bg-white ring-1 ring-slate-200 px-3 py-2.5">
                <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-none">
                  {f.thumbnail
                    ? <img src={f.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <Paperclip size={15} className="text-slate-400" />}
                </span>
                <span className="text-[13.5px] text-slate-700 truncate flex-1 min-w-0">{f.name || f.location}</span>
                <button
                  type="button"
                  onClick={() => onScan(f.location)}
                  disabled={busy || !!ocrBusy}
                  className={`rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition flex-none inline-flex items-center gap-1.5 disabled:opacity-50 ${
                    done
                      ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      : 'bg-brand-600 text-white hover:bg-brand-700'
                  }`}
                >
                  {busy
                    ? <><Loader2 size={13} className="animate-spin" /> กำลังอ่าน…</>
                    : done
                      ? <><Check size={13} strokeWidth={2.5} /> อ่านอีกครั้ง</>
                      : <><ScanLine size={13} strokeWidth={2.5} /> อ่านข้อมูลจากเอกสาร</>}
                </button>
              </div>
            )
          })}

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full rounded-xl border border-dashed border-brand-300 bg-white/60 hover:bg-white transition py-2.5 text-[12.5px] text-slate-500 disabled:opacity-50"
          >
            {uploading
              ? <><Loader2 size={13} className="inline animate-spin mr-1.5" /> กำลังอัปโหลด…</>
              : '+ แนบเอกสารเพิ่ม'}
          </button>
        </div>
      )}

      {(err || ocrError) && (
        <div className="mt-2.5 text-[12.5px] text-rose-600 flex items-start gap-1">
          <AlertCircle size={14} className="mt-0.5 flex-none" />
          <span className="break-words">{err || ocrError}</span>
        </div>
      )}

      {ocrBusy && (
        <div className="mt-2.5 text-[12px] text-slate-500">
          อ่านเอกสารใช้เวลาประมาณ 20–30 วินาที
        </div>
      )}

      {anyRead && !ocrBusy && (
        <div className="mt-2.5 text-[12px] text-emerald-700 flex items-center gap-1.5">
          <Check size={13} strokeWidth={2.5} /> กรอกข้อมูลให้แล้ว — ตรวจสอบด้านล่างก่อนบันทึก
        </div>
      )}
    </section>
  )
}
